import { Button, InputWrapper } from '@vrplatform/components';
import { captureSentryError } from '@vrplatform/data';
import { Icon } from '@vrplatform/icons';
import { showErrorNotification } from '@vrplatform/theme';
import { showWarnNotification, useAppName } from '@vrplatform/theme';
import {
  Anchor,
  Box,
  Card,
  Collapse,
  Modal,
  ScrollArea,
  Stack,
  Text,
  rem,
  useMantineColorScheme,
  useMantineTheme,
} from '@mantine/core';
import { Dropzone, type FileWithPath, MIME_TYPES } from '@mantine/dropzone';
import { useDisclosure } from '@mantine/hooks';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import type { ZodSchema } from 'zod';
import { getCsvUrl } from '../../utils';
import type { CsvRowError } from './_types';
import { parseCsv } from './parse-csv';

type CsvParsingHandler = (file: File) => ReturnType<typeof parseCsv>;

type FormInputs = {
  file: FileWithPath;
};

type ModalType = 'owners';

const transformFile = async (file: FileWithPath) => {
  const reader = new FileReader();

  return await new Promise<string>((resolve, reject) => {
    reader.onload = () => {
      resolve(reader.result as string);
    };
    // PRO-12792: normalize FileReader failures to Error objects.
    reader.onerror = () => {
      reject(reader.error ?? new Error('Failed to read CSV file.'));
    };
    reader.onabort = () => {
      reject(new Error('CSV file read was aborted.'));
    };
    reader.readAsText(file);
  });
};

const isFileReadError = (message: string | undefined) => {
  const lower = message?.toLowerCase() || '';
  return (
    lower.includes('failed to read csv file') ||
    lower.includes('csv file read was aborted')
  );
};

export const LegacyCsvImportModal = <
  TRow extends Record<string, string | null | boolean | number>,
>({
  closeModal: cl,
  opened,
  title,
  type,
  defaultRows,
  handleResult,
  zodSchema,
  documentationUrl,
}: {
  opened: boolean;
  closeModal: () => void;
  title: string;
  type: ModalType;
  handleResult: (result: TRow[]) => Promise<{ ok: boolean; message?: string }>;
  defaultRows: TRow[];
  zodSchema: ZodSchema | ((input: TRow) => ZodSchema);
  documentationUrl: string;
}) => {
  const { appName } = useAppName();

  const theme = useMantineTheme();
  const methods = useForm<FormInputs>();

  const templateHref = useMemo(() => {
    return getCsvUrl(defaultRows);
  }, [defaultRows]);

  const closeModal = useCallback(() => {
    methods.reset({
      file: undefined,
    });
    methods.clearErrors();
    cl();
  }, [methods, cl]);

  const submit = useCallback(
    async (data: FormInputs) => {
      try {
        const fileData = await transformFile(data.file);

        if (typeof fileData !== 'string') {
          throw new Error('Failed to parse CSV to string');
        }

        const result = await parseCsv<TRow>({
          fileData,
          schema: zodSchema,
        });

        if (!result.success) {
          throw new Error('Failed to parse CSV');
        }

        const res = await handleResult(result.data);

        if (res.ok) {
          closeModal();
        } else {
          throw new Error(res.message);
        }
      } catch (error: any) {
        if (!isFileReadError(error?.message)) {
          captureSentryError(error);
        }
        showWarnNotification({
          title: 'Invalid file',
          message: error?.message || 'Failed to parse CSV file.',
        });
      }
    },
    [handleResult, closeModal, zodSchema]
  );

  const handleParsing: CsvParsingHandler = useCallback(
    async (file: FileWithPath) => {
      const result = await transformFile(file);

      if (typeof result !== 'string') {
        return {
          success: false,
          errors: [{ rowNumber: 0, message: 'Failed to parse CSV' }],
        };
      }

      return parseCsv({
        fileData: result,
        schema: zodSchema,
      });
    },
    [zodSchema]
  );

  useEffect(() => {
    if (!opened)
      methods.reset({
        file: undefined,
      });
  }, [opened, methods.reset]);

  return (
    <Modal
      opened={opened}
      onClose={closeModal}
      title={title}
      size={500}
      centered
      styles={{
        title: {
          fontWeight: 500,
          fontSize: theme.fontSizes.lg,
        },
        content: {
          padding: theme.spacing.sm,
        },
      }}
    >
      <Text component="p" c="neutral">
        Upload a CSV file to add {type} to {appName}. Your CSV should include
        header and follow specified structure.
      </Text>

      <Text component="p" c="neutral" mb="md">
        <Anchor href={templateHref} download={`${type}_import_template.csv`}>
          Download a CSV Template
        </Anchor>{' '}
        or read{' '}
        <Anchor
          href={documentationUrl}
          target="_blank"
          rel="noopener,noreferrer"
        >
          documentation
        </Anchor>
        .
      </Text>

      <Box
        component="form"
        onSubmit={methods.handleSubmit(submit)}
        onReset={closeModal}
      >
        <Controller
          control={methods.control}
          name="file"
          rules={{
            required: 'Please upload a file.',
            validate: (value) => {
              if (!value) return 'Please upload a file.';
              if (value.type !== MIME_TYPES.csv) return 'File must be CSV.';
              return true;
            },
          }}
          render={({ field, fieldState }) => {
            return (
              <InputWrapper
                label={'Upload CSV'}
                error={fieldState.error?.message}
              >
                <DropzoneInput
                  type={type}
                  onChange={field.onChange}
                  resetFile={() =>
                    methods.reset({
                      file: undefined,
                    })
                  }
                  file={field.value}
                  error={!!fieldState.error}
                  handleParsing={handleParsing}
                />
              </InputWrapper>
            );
          }}
        />

        <Box
          mt="lg"
          sx={(theme) => ({
            display: 'flex',
            flexWrap: 'nowrap',
            gap: theme.spacing.sm,
          })}
        >
          <Button type="reset" disabled={methods.formState.isSubmitting}>
            Cancel
          </Button>
          <Button
            disabled={!methods.formState.isValid}
            loading={methods.formState.isSubmitting}
            type="submit"
            variant="primary"
            sx={{
              flex: 1,
            }}
          >
            Import {type}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

const DropzoneInput = ({
  type,
  onChange,
  error,
  file,
  handleParsing,
  resetFile,
}: {
  type: ModalType;
  handleParsing: CsvParsingHandler;
  onChange: (file: FileWithPath) => void;
  error: boolean;
  resetFile: () => void;
  file: FileWithPath | undefined;
}) => {
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const [loading, setLoading] = useState(false);
  const [rowErrors, setRowErrors] = useState<CsvRowError[]>([]);
  const [rowCount, setRowCount] = useState<number>(0);
  const [opened, handlers] = useDisclosure(false);

  const iconSize = 30;
  const inputHeight = 105;

  const resetDropZone = () => {
    resetFile();
    setRowErrors([]);
    setRowCount(0);
  };

  return (
    <>
      <Dropzone
        loading={loading}
        onFileDialogOpen={resetDropZone}
        onDrop={async (files) => {
          const file = files[0];
          resetFile();
          setRowCount(0);
          setLoading(true);
          try {
            const res = await handleParsing(file);
            if (res.success) {
              onChange(file);
              setRowCount(res.data.length);
              setRowErrors([]);
            } else {
              setRowErrors(res.errors);
            }
          } catch (error: any) {
            resetFile();
            showErrorNotification({
              title: 'Invalid file',
              message: error?.message || 'Failed to parse CSV file.',
            });
          } finally {
            setLoading(false);
          }
        }}
        onReject={(files) => {
          const file = files?.[0];

          const message =
            file?.errors?.[0]?.message || 'Please upload a valid CSV file.';

          const isFileTypeError =
            message.toLowerCase() === 'file type must be text/csv';

          if (!isFileTypeError) captureSentryError(message);

          showErrorNotification({
            title: 'Invalid file',
            message,
          });
        }}
        maxSize={3 * 1024 ** 2}
        accept={[MIME_TYPES.csv]}
        multiple={false}
        sx={(theme) => {
          const errorBorderColor =
            theme.colors.red[colorScheme === 'dark' ? 4 : 6];
          const defaultBorderColor =
            theme.colors.neutral[colorScheme === 'dark' ? 4 : 2];

          return {
            borderColor: error ? errorBorderColor : defaultBorderColor,
            borderWidth: '2px',
            borderRadius: rem(6),
            borderStyle: 'dashed',
            paddingBlock: theme.spacing.md,
            marginTop: rem(5),
            '&[data-accept]': {
              borderColor: theme.colors[theme.primaryColor][6],
              backgroundColor:
                theme.colors.neutral[colorScheme === 'dark' ? 3 : 0],
            },
            '&[data-reject]': {
              borderColor: errorBorderColor,
            },
          };
        }}
      >
        <Stack
          gap="md"
          align="center"
          justify="center"
          style={{ minHeight: rem(inputHeight), pointerEvents: 'none' }}
        >
          <Dropzone.Accept>
            <Icon
              size={iconSize}
              strokeWidth={1}
              icon="CheckCircleIcon"
              color={
                theme.colors[theme.primaryColor][colorScheme === 'dark' ? 4 : 6]
              }
            />
          </Dropzone.Accept>
          <Dropzone.Reject>
            <Icon
              size={iconSize}
              strokeWidth={1}
              icon="AlertTriangleIcon"
              color={theme.colors.red[colorScheme === 'dark' ? 4 : 6]}
            />
          </Dropzone.Reject>
          <Dropzone.Idle>
            <Icon
              icon={file ? 'FileTextIcon' : 'ImportIcon'}
              size={iconSize}
              strokeWidth={1}
            />
          </Dropzone.Idle>

          {file ? (
            <div>
              <Text
                size="sm"
                ta="center"
                fw={500}
                color={theme.colors[theme.primaryColor][6]}
              >
                {file.name}
                <br />
                <Text
                  color={theme.colors.gray[6]}
                  component="span"
                  display="inline"
                  size="sm"
                  ta="center"
                >
                  {rowCount} {type} found
                </Text>
              </Text>
            </div>
          ) : (
            <div>
              <Text size="sm" color="dimmed" mt={7} ta="center">
                Drag and drop {type} CSV file
                <br />
                or{' '}
                <Text
                  color={theme.colors[theme.primaryColor][6]}
                  component="span"
                  display="inline"
                >
                  select file on disk
                </Text>
              </Text>
            </div>
          )}
        </Stack>
      </Dropzone>

      {!!rowErrors.length && (
        <Card
          mt="sm"
          sx={(theme) => ({
            backgroundColor: theme.colors.red[colorScheme === 'dark' ? 3 : 0],
          })}
        >
          <Text component="p" m={0} fw={500} size="sm" mb="xs">
            Found {rowErrors.length} errors in the file
          </Text>
          <Button
            type="button"
            size="xs"
            onClick={(e) => {
              e.preventDefault();
              handlers.toggle();
            }}
            leftIcon={'FileTextIcon'}
            sx={{
              backgroundColor: 'transparent',
              ':hover': {
                backgroundColor: 'transparent',
              },
            }}
          >
            View errors
          </Button>
          <Collapse in={opened}>
            <ScrollArea
              h={rem(inputHeight + 50)}
              mt="xs"
              px={'xs'}
              sx={{
                backgroundColor: '#ffffff80',
              }}
            >
              {rowErrors.map((error, index) => (
                <Text key={index} color="neutral" component="p" size="xs" m={0}>
                  {error.rowNumber === 0 ? 'Header' : `Row ${error.rowNumber}`}:{' '}
                  {error.message}
                </Text>
              ))}
            </ScrollArea>
          </Collapse>
        </Card>
      )}
    </>
  );
};
